import {List} from 'immutable';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {WithRouterProps} from 'react-router';

import Assignment from './Assignment';
import AssignmentIcon from './AssignmentIcon';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import AssignmentStatusDropdown, {Value as DropdownValue} from './AssignmentStatusDropdown';
import {LoadingSpinner, Text} from '@albert-io/atomic';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';
import toDoAssignmentsQuery from '../queries/toDoAssignmentsQuery';

interface Props extends WithRouterProps {}

export default function StudentHomepageAssignmentsTabContent({params}: Props) {
  const {student, updateAssignmentsLastFetchedAt} = useStudentDashboard(
    useShallow((state) => ({
      student: state.student,
      updateAssignmentsLastFetchedAt: state.updateAssignmentsLastFetchedAt
    }))
  );

  const [assignments, setAssignments] = useState<List<AssignmentModelV3> | null>(null);
  const [dropdownValue, setDropdownValue] = useState<DropdownValue>('toDo');

  const classroomId: string | undefined = params.classroomId;

  const isLoading = assignments === null;

  useEffect(() => {
    toDoAssignmentsQuery(student.getId(), classroomId)
      .getResourcePromise()
      .then((fetchedAssignments) => {
        if (classroomId === undefined) {
          updateAssignmentsLastFetchedAt();
        }

        setAssignments(
          fetchedAssignments.sort(
            (assignmentA, assignmentB) =>
              moment(assignmentA.getDueDateFromSettings()).valueOf() -
              moment(assignmentB.getDueDateFromSettings()).valueOf()
          )
        );
      })
      .catch((error) => {
        console.error(error);
        // TODO: better error handling
      });
  }, [classroomId, student, updateAssignmentsLastFetchedAt]);

  return (
    <div className='relative flex h-full flex-col gap-x2 px-x4 py-x3'>
      <AssignmentStatusDropdown
        className='mb-x1'
        disabled={isLoading}
        isScopedToClassroom={classroomId !== undefined}
        onValueChange={setDropdownValue}
        value={dropdownValue}
      />
      {isLoading && (
        <div className='absolute left-x0 flex h-full w-full items-center justify-center'>
          <LoadingSpinner bottomMarginLevel={0} size={3} />
        </div>
      )}
      {!isLoading && assignments.size === 0 && (
        <div className='flex flex-1 flex-col items-center justify-center gap-x2 border border-primary p-x4'>
          <AssignmentIcon backgroundColorClass='background-bold' iconColor='tertiary' />
          <Text color='secondary'>Your assignments will appear here</Text>
        </div>
      )}
      {!isLoading &&
        assignments.size > 0 &&
        assignments.map((assignment) => (
          <Assignment assignment={assignment} key={assignment.getId()} />
        ))}
    </div>
  );
}
